import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../lib/apiContext';
import { queryKeys } from '../../lib/queryKeys';
import { isJobTerminal } from './aggregate';
import type {
  FleetStatusResponse,
  FleetUpdatesRequest,
  FleetUpdatesResponse,
  Job,
  JobsResponse,
} from '../../lib/types';

/**
 * GET /fleet/status?refresh=1 — per-site status for every fleet site.
 *
 * ⛔ `refresh=1` is REQUIRED here, not an optimization. Without it the gateway
 * serves the stored snapshot, which carries summary columns only — the store
 * has never held a plugin inventory — so plugins[] would come back empty and
 * the Updates hub would render "all sites are up to date" while updates were
 * actually pending. The hub aggregates the inventory, so it must fan out.
 */
export function useFleetStatus() {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.fleetStatus,
    queryFn: () => client.get<FleetStatusResponse>('/fleet/status?refresh=1'),
  });
}

/** GET /jobs — recent jobs, newest first (the history table). */
export function useJobs(limit = 50) {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.jobs,
    queryFn: () => client.get<JobsResponse>(`/jobs?limit=${limit}`),
  });
}

/**
 * Operator-triggered fleet refresh. GET /fleet/status is the fan-out that
 * re-probes every site, so calling it and then invalidating the fleet listing
 * is what "Sync now" means. Same endpoint as useFleetStatus — imperative
 * (mutation) rather than declarative so the button gets a pending state.
 */
export function useSyncFleet() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => client.get<FleetStatusResponse>('/fleet/status?refresh=1'),
    onSuccess: (data) => {
      // The fan-out response IS the fresh status — seed it so the updates hub
      // doesn't re-request what we just fetched.
      queryClient.setQueryData(queryKeys.fleetStatus, data);
    },
    // `exact` matters — fleetStatus is a prefix child of fleet; a loose
    // invalidate would discard the status data seeded above.
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.fleet, exact: true }),
  });
}

/** POST /fleet/updates — enqueue a bulk update job. */
export function useSubmitUpdates() {
  const client = useApiClient();
  return useMutation({
    mutationFn: (body: FleetUpdatesRequest) =>
      client.post<FleetUpdatesResponse>('/fleet/updates', body),
  });
}

/**
 * Await one job to a terminal state. Used to SEQUENCE multi-kind submissions
 * (plugins → theme → core on one site): running them concurrently would race
 * two WordPress upgraders against the same install.
 *
 * Separate from useJob, which polls for DISPLAY — this is the gate, and reading
 * the endpoint directly avoids racing React state between submissions.
 */
export function useAwaitJob() {
  const client = useApiClient();
  // Stops polling on unmount — otherwise the loop outlives the screen and keeps hitting a torn-down app.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  return async function awaitJob(
    jobId: string,
    { intervalMs = 1500, timeoutMs = 15 * 60 * 1000 } = {},
  ): Promise<Job | null> {
    const deadline = Date.now() + timeoutMs;
    while (alive.current) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
      if (!alive.current) return null;
      const job = await client.get<Job>(`/jobs/${jobId}`);
      if (isJobTerminal(job.status)) return job;
      // Never wedge the caller's sequential queue on a job that never terminates.
      if (Date.now() > deadline) return null;
    }
    return null;
  };
}

/**
 * GET /jobs/{id} — poll a running update job until it reaches a terminal state.
 * enabled only when a jobId is present; polling stops on succeeded/failed/partial.
 *
 * `invalidateSiteId` re-derives that site's pending list once the job finishes:
 * after updating a plugin the drill-down must stop offering the update it just
 * applied. Omitted on the fleet hub, which has no single site to invalidate.
 */
export function useJob(jobId: string | null, invalidateSiteId?: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const settled = useRef<string | null>(null);

  const query = useQuery({
    queryKey: queryKeys.job(jobId ?? ''),
    queryFn: () => client.get<Job>(`/jobs/${jobId}`),
    enabled: jobId !== null && jobId !== '',
    refetchInterval: (q) => {
      const job = q.state.data as Job | undefined;
      if (job && isJobTerminal(job.status)) return false;
      return 1500;
    },
  });

  // Fire ONCE per job on the transition into terminal — not on every render
  // while a terminal job stays on screen (would refetch forever).
  const status = query.data?.status;
  useEffect(() => {
    if (!jobId || !status || !isJobTerminal(status)) return;
    if (settled.current === jobId) return;
    settled.current = jobId;
    queryClient.invalidateQueries({ queryKey: queryKeys.jobs });
    if (invalidateSiteId) {
      queryClient.invalidateQueries({ queryKey: queryKeys.siteStatus(invalidateSiteId) });
    }
  }, [jobId, status, invalidateSiteId, queryClient]);

  return query;
}
